import type {
  DefaultMeetingSession,
  AudioVideoEventAttributes,
  EventName,
  MeetingSessionStatus,
} from 'amazon-chime-sdk-js';
import { MeetingSessionStatusCode } from 'amazon-chime-sdk-js';

import {
  adaptHearing,
  determineCallDirection,
  getHearingPhoneNumber,
} from './VoiceMeetingServiceHelpers';

import type { Maybe } from 'common/types';
import {
  detachChimeEventController,
  isChimeSessionTearingDown,
  setChimeSessionTearingDown,
} from 'features/voice-meeting/helpers';
import {
  initialTeamingSpeakerContext,
  type TeamingSpeakerContext,
  type VoiceSessionHearingParticipant,
} from 'features/voice-meeting/interfaces';
import { VoiceMeetingEventBus } from 'features/voice-meeting/services';
import type {
  Participant,
  SessionHearingParticipant,
  VoiceSessionResponse,
} from 'features/voice-session/interfaces';

// Chime lifecycle and media-health events forwarded for analytics. A retried
// first attempt emits no failure event, so meetingStartSucceeded and
// meetingReconnected are the only signals that a connect needed a retry.
// These events go to telemetry only; meetingError$ stays limited to the
// meeting-start and meeting failures that tear the session down.
const CHIME_TELEMETRY_EVENTS: readonly EventName[] = [
  'meetingStartRequested',
  'meetingStartSucceeded',
  'meetingReconnected',
  'meetingStartFailed',
  'meetingFailed',
  'meetingEnded',
  'signalingDropped',
  'receivingAudioDropped',
  'sendingAudioFailed',
  'sendingAudioRecovered',
  'audioInputFailed',
  'videoInputFailed',
];

/**
 * VoiceMeetingService manages voice meeting sessions and participants
 */
export class VoiceMeetingService {
  private static instance: VoiceMeetingService;
  public meetingSession: DefaultMeetingSession | null = null;
  public voiceSession: VoiceSessionResponse | null = null;
  private agentNumber = '';
  private teamingSpeakerContext: TeamingSpeakerContext =
    initialTeamingSpeakerContext;

  private eventObserver: any = null;
  private audioVideoObserver: any = null;
  private attendeeIdPresenceHandler:
    | ((
        attendeeId: string,
        present: boolean,
        externalUserId?: string,
        dropped?: boolean
      ) => void)
    | null = null;
  private fatalErrorHandler: ((error: Error) => void) | null = null;
  private transcriptEventHandler: ((event: unknown) => void) | null = null;
  // signalingDropped can fire repeatedly while the socket is down. Only the
  // first drop is forwarded until meetingReconnected re-arms it.
  private signalingDroppedReported = false;

  private constructor() {}

  /**
   * Returns the singleton instance of the voice meeting service
   * @returns The VoiceMeetingService instance
   */
  public static getInstance(): VoiceMeetingService {
    if (!VoiceMeetingService.instance) {
      VoiceMeetingService.instance = new VoiceMeetingService();
    }

    return VoiceMeetingService.instance;
  }

  // ----------------------
  // Session Management
  // ----------------------

  /**
   * Sets the current meeting session
   * @param session - The meeting session to set
   */
  public setMeetingSession(session: DefaultMeetingSession | null): void {
    this.meetingSession = session;
  }

  /**
   * Sets the current voice session
   * @param sessionState - The voice session state to set
   */
  public setVoiceSession = (voiceSession: VoiceSessionResponse): void => {
    // Set the new voice session
    this.voiceSession = voiceSession;
  };

  public setAgentNumber = (agentNumber: string): void => {
    this.agentNumber = agentNumber;
  };

  public setTeamingSpeakerContext = (
    teamingSpeakerContext: TeamingSpeakerContext
  ): void => {
    this.teamingSpeakerContext = teamingSpeakerContext;
  };

  /**
   * Returns the session ID from the current voice session
   * @returns The external meeting ID
   */
  public getSessionId = (): string =>
    this.voiceSession?.chimeMeeting.ExternalMeetingId;

  /**
   * Tears down this client's local meeting session state.
   * Returns a promise that resolves after audioVideoDidStop and destroy.
   */
  public tearDownLocalMeetingSession = (): Promise<void> => {
    const meetingSession = this.meetingSession;
    const audioVideo = meetingSession?.audioVideo;

    if (!audioVideo || !meetingSession) {
      return Promise.resolve();
    }

    setChimeSessionTearingDown(true);
    this.tearDownListeners();

    // Clear the shared DeviceController's EventController before destroy so
    // in-flight start/stopAudioInput cannot publish against a torn-down
    // configuration, and so the next session can rebind a live controller.
    // See: https://github.com/aws/amazon-chime-sdk-js/issues/2178
    detachChimeEventController();

    // Destroy meeting session after audioVideo stopped. See: https://github.com/aws/amazon-chime-sdk-js/issues/2178
    return new Promise((resolve) => {
      const finishTeardown = () => {
        detachChimeEventController();
        setChimeSessionTearingDown(false);
        resolve();
      };

      const destroyObserver = {
        audioVideoDidStop() {
          audioVideo.removeObserver(destroyObserver);
          void Promise.resolve(meetingSession.destroy()).finally(
            finishTeardown
          );
        },
      };

      audioVideo.addObserver(destroyObserver);
      audioVideo.stop();
      this.meetingSession = null;
      this.voiceSession = null;
    });
  };

  /**
   * Clears all participants from the voice session
   */
  public clearParticipants = (): void => {
    if (this.voiceSession) {
      this.voiceSession.participants = [];
    }
  };

  // ----------------------
  // Hearing Participant Methods
  // ----------------------

  /**
   * Gets a hearing participant by ID
   * @param id - The ID of the hearing participant to find
   * @returns The hearing participant or null if not found
   */
  public getHearingById = (
    id: string
  ): VoiceSessionHearingParticipant | null => {
    const hearing = this.voiceSession?.participants.find(
      (participant) => participant.type === 'hearing' && participant.id === id
    ) as Maybe<SessionHearingParticipant>;

    if (!hearing) {
      return null;
    }

    return adaptHearing(hearing);
  };

  /**
   * Gets a voice session participant by ID
   * @param id - The participant ID (Chime externalUserId)
   * @returns The participant or null if not found
   */
  public getParticipantById = (id: string): Participant | null => {
    const participant = this.voiceSession?.participants.find(
      (entry) => entry.id === id
    );

    return participant ?? null;
  };

  /**
   * Gets a hearing participant by phone number
   * @param hearingPhoneNumber - The phone number to search for
   * @returns The hearing participant or null if not found
   */
  public getHearingByPhoneNumber = (
    hearingPhoneNumber: string
  ): VoiceSessionHearingParticipant | null => {
    const hearing = this.voiceSession?.participants.find(
      (participant) =>
        participant.type === 'hearing' &&
        getHearingPhoneNumber(participant) === hearingPhoneNumber
    ) as Maybe<SessionHearingParticipant>;

    if (!hearing) {
      return null;
    }

    return adaptHearing(hearing);
  };

  /**
   * Gets the first hearing participant that's not in disconnected state
   * @param participantId - Optional participant ID to filter by
   * @returns The first active hearing participant or null if none found
   */
  public getFirstNotDisconnectedHearing = (
    participantId?: string
  ): VoiceSessionHearingParticipant | null => {
    const participants: Participant[] = Object.assign(
      [],
      this.voiceSession?.participants
    ).reverse();

    const hearingParticipant = participants.find((participant) => {
      const baseSelector =
        participant.type === 'hearing' && participant.state !== 'disconnected';

      if (participantId) {
        return baseSelector && participant.id === participantId;
      }

      return baseSelector;
    }) as Maybe<SessionHearingParticipant>;

    return hearingParticipant ? adaptHearing(hearingParticipant) : null;
  };

  /**
   * Gets all hearing participants in the session
   * @returns Array of all hearing participants
   */
  public getHearings = (): VoiceSessionHearingParticipant[] => {
    const participants: Participant[] = this.voiceSession?.participants || [];

    const hearingParticipants = participants.filter((participant) => {
      return participant.type === 'hearing';
    });

    return hearingParticipants.map(adaptHearing);
  };

  /**
   * Gets all hearing participants that are not in disconnected state
   * @returns Array of active hearing participants
   */
  public getAllHearingsNotDisconnected =
    (): VoiceSessionHearingParticipant[] => {
      const participants: Participant[] = this.voiceSession?.participants || [];

      const hearingParticipants = participants.filter((participant) => {
        return (
          participant.type === 'hearing' && participant.state !== 'disconnected'
        );
      }) as SessionHearingParticipant[];

      return hearingParticipants.map(adaptHearing);
    };

  /**
   * Gets the first hearing participant in the session
   * @returns The first hearing participant or null if none found
   */
  public getFirstHearing = (): VoiceSessionHearingParticipant | null => {
    const hearing = this.voiceSession?.participants.find(
      (participant) => participant.type === 'hearing'
    ) as Maybe<SessionHearingParticipant>;

    if (!hearing) {
      return null;
    }

    return adaptHearing(hearing);
  };

  /**
   * Gets the direction of the current call
   * @param hearingId - Optional specific hearing ID to check
   * @returns The call direction or null if no session exists
   */
  public getCallDirection = (
    hearingId?: string
  ): 'inbound' | 'outbound' | null => {
    if (!this.voiceSession) {
      return null;
    }

    return determineCallDirection(this.voiceSession, hearingId);
  };

  // ----------------------
  // Event Handling
  // ----------------------

  /**
   * Sets up all event listeners for the meeting session
   * @throws Error if meeting session is not found
   */
  public setupListeners = (): void => {
    if (!this.meetingSession) {
      throw Error('DEBUG: Meeting session not found');
    }
    this.tearDownListeners();
    this.addEventObserver();
    this.addAudioVideoObserver();
    this.subscribeToAttendeeIdPresence();
    this.realtimeSubscribeToFatalError();
    this.subscribeToTranscriptionEvents();
  };

  /**
   * Removes all event listeners from the meeting session
   */
  public tearDownListeners = (): void => {
    if (!this.meetingSession) {
      return;
    }

    // Remove event controller observer
    if (this.eventObserver) {
      this.meetingSession.eventController.removeObserver(this.eventObserver);
      this.eventObserver = null;
    }

    // Remove audio video observer
    if (this.audioVideoObserver) {
      this.meetingSession.audioVideo.removeObserver(this.audioVideoObserver);
      this.audioVideoObserver = null;
    }

    // Unsubscribe from attendee ID presence
    if (this.attendeeIdPresenceHandler) {
      this.meetingSession.audioVideo.realtimeUnsubscribeToAttendeeIdPresence(
        this.attendeeIdPresenceHandler
      );
      this.attendeeIdPresenceHandler = null;
    }

    // Unsubscribe from fatal errors
    if (this.fatalErrorHandler) {
      this.meetingSession.audioVideo.realtimeUnsubscribeToFatalError(
        this.fatalErrorHandler
      );
      this.fatalErrorHandler = null;
    }

    if (this.transcriptEventHandler) {
      this.meetingSession.audioVideo.transcriptionController?.unsubscribeFromTranscriptEvent?.(
        this.transcriptEventHandler
      );
      this.transcriptEventHandler = null;
    }
  };

  /**
   * Registers a callback for when audio/video has started
   * @param callback - The function to call when audio/video starts
   */
  public onAudioVideoDidStart = (callback: () => void): void => {
    this.meetingSession?.audioVideo.addObserver({
      audioVideoDidStart: callback,
    });
  };

  /**
   * Sets up event observer for meeting events
   * @private
   */
  private readonly addEventObserver = (): void => {
    this.signalingDroppedReported = false;
    this.eventObserver = {
      eventDidReceive: (
        name: EventName,
        attributes: AudioVideoEventAttributes
      ) => {
        const meetingEventErrors = ['meetingStartFailed', 'meetingFailed'];
        if (meetingEventErrors.includes(name)) {
          VoiceMeetingEventBus.meetingError$.next({
            name,
            attributes,
          });
        }

        if (name === 'meetingReconnected') {
          this.signalingDroppedReported = false;
        }

        if (name === 'signalingDropped') {
          if (this.signalingDroppedReported) {
            return;
          }
          this.signalingDroppedReported = true;
        }

        // Failures during an intentional stop are expected noise. meetingEnded
        // still forwards so the end of the meeting is recorded.
        const isExpectedTeardownNoise =
          isChimeSessionTearingDown() && name !== 'meetingEnded';
        if (CHIME_TELEMETRY_EVENTS.includes(name) && !isExpectedTeardownNoise) {
          VoiceMeetingEventBus.meetingTelemetry$.next({ name, attributes });
        }
      },
    };
    this.meetingSession!.eventController.addObserver(this.eventObserver);
  };

  /**
   * Sets up audio/video observer for session events
   * @private
   */
  private readonly addAudioVideoObserver = (): void => {
    this.audioVideoObserver = {
      audioVideoDidStop: (sessionStatus?: MeetingSessionStatus) => {
        // Intentional teardown already owns cleanup; ignore stop callbacks
        // that fire as a side effect of audioVideo.stop().
        if (isChimeSessionTearingDown()) {
          return;
        }

        if (
          sessionStatus &&
          VoiceMeetingService.isMeetingSessionFailed(sessionStatus)
        ) {
          VoiceMeetingEventBus.fatalError$.next(
            new Error('Meeting session failed')
          );
          return;
        }

        VoiceMeetingEventBus.finishAndDeleteVoiceSession();
      },
    };
    this.meetingSession?.audioVideo.addObserver(this.audioVideoObserver);
  };

  /**
   * Determines if a meeting session has failed based on status code
   * @param sessionStatus - The meeting session status to check
   * @returns True if the session has failed
   * @private
   */
  private static isMeetingSessionFailed(
    sessionStatus: MeetingSessionStatus
  ): boolean {
    return (
      sessionStatus.statusCode() ===
        MeetingSessionStatusCode.AudioInternalServerError ||
      sessionStatus.statusCode() ===
        MeetingSessionStatusCode.AudioServiceUnavailable
    );
  }

  private readonly subscribeToTranscriptionEvents = (): void => {
    if (this.transcriptEventHandler) {
      return;
    }

    this.transcriptEventHandler = (event) => {
      if (
        !event ||
        typeof event !== 'object' ||
        !('results' in event) ||
        !Array.isArray(event.results)
      ) {
        return;
      }

      for (const transcriptResult of event.results) {
        const result = transcriptResult as unknown as Record<string, unknown>;
        const text = this.getTranscriptText(result);

        if (!text) {
          continue;
        }

        const { speakerRole, speakerLabel } =
          this.resolveTranscriptSpeaker(result);

        VoiceMeetingEventBus.transcriptCaption$.next({
          resultId:
            typeof result.resultId === 'string'
              ? result.resultId
              : `transcript-${Date.now()}`,
          text,
          isPartial: Boolean(result.isPartial),
          languageCode:
            typeof result.languageCode === 'string'
              ? result.languageCode
              : undefined,
          speakerRole,
          speakerLabel,
          startedAtMs:
            typeof result.startTimeMs === 'number'
              ? result.startTimeMs
              : undefined,
          endedAtMs:
            typeof result.endTimeMs === 'number' ? result.endTimeMs : undefined,
        });
      }
    };

    this.meetingSession?.audioVideo.transcriptionController?.subscribeToTranscriptEvent(
      this.transcriptEventHandler
    );
  };

  private readonly getTranscriptText = (
    result: Record<string, unknown>
  ): string | null => {
    const alternatives = Array.isArray(result.alternatives)
      ? (result.alternatives as Record<string, unknown>[])
      : [];

    if (!alternatives.length) {
      return null;
    }

    const transcript = alternatives.find(
      (alternative) => typeof alternative.transcript === 'string'
    )?.transcript as Maybe<string>;

    return transcript?.trim() || null;
  };

  private readonly getParticipantByAttendeeId = (
    attendeeId: string
  ): Participant | null => {
    const participant = this.voiceSession?.participants.find(
      (entry) => entry.chimeAttendee?.attendeeId === attendeeId
    );

    return participant ?? null;
  };

  private readonly formatInterpreterLabel = (agentId: string): string => {
    const normalizedAgentId = agentId.trim();

    if (!normalizedAgentId) {
      return 'Interpreter';
    }

    return `Interpreter ${normalizedAgentId}`;
  };

  private readonly resolveTeamingInterpreterSpeaker = (): {
    speakerRole: 'remote';
    speakerLabel: string;
  } | null => {
    const { isActive, otherInterpreterAgentId } = this.teamingSpeakerContext;

    if (!isActive || !otherInterpreterAgentId.trim()) {
      return null;
    }

    return {
      speakerRole: 'remote',
      speakerLabel: this.formatInterpreterLabel(otherInterpreterAgentId),
    };
  };

  private readonly resolveSpeakerFromParticipant = (
    participant: Participant
  ): {
    speakerRole: 'local' | 'remote' | 'unknown';
    speakerLabel: string;
  } | null => {
    if (participant.type === 'hearing') {
      const hearingParticipant = adaptHearing(participant);

      return {
        speakerRole: 'remote',
        speakerLabel: hearingParticipant.phoneNumber,
      };
    }

    if (participant.type === 'agent' && !this.teamingSpeakerContext.isActive) {
      return {
        speakerRole: 'remote',
        speakerLabel: this.formatInterpreterLabel(participant.agentId),
      };
    }

    return null;
  };

  private readonly resolveTranscriptSpeaker = (
    result: Record<string, unknown>
  ): { speakerRole: 'local' | 'remote' | 'unknown'; speakerLabel: string } => {
    const meetingSession = this.meetingSession as Maybe<{
      configuration?: { credentials?: { attendeeId?: string } };
    }>;

    const localAttendeeId =
      meetingSession?.configuration?.credentials?.attendeeId;
    const { attendeeId, externalUserId } =
      this.extractTranscriptSpeakerIdentity(result);

    if (localAttendeeId && attendeeId && localAttendeeId === attendeeId) {
      return {
        speakerRole: 'local',
        speakerLabel: this.resolveLocalSpeakerLabel(),
      };
    }

    if (externalUserId) {
      const participant = this.getParticipantById(externalUserId);
      const resolvedSpeaker = participant
        ? this.resolveSpeakerFromParticipant(participant)
        : null;

      if (resolvedSpeaker) {
        return resolvedSpeaker;
      }
    }

    if (attendeeId) {
      const participant = this.getParticipantByAttendeeId(attendeeId);
      const resolvedSpeaker = participant
        ? this.resolveSpeakerFromParticipant(participant)
        : null;

      if (resolvedSpeaker) {
        return resolvedSpeaker;
      }
    }

    if (attendeeId || externalUserId) {
      const teamingSpeaker = this.resolveTeamingInterpreterSpeaker();

      if (teamingSpeaker) {
        return teamingSpeaker;
      }
    }

    return {
      speakerRole: 'unknown',
      speakerLabel: 'Unknown',
    };
  };

  private readonly extractTranscriptSpeakerIdentity = (
    result: Record<string, unknown>
  ): {
    attendeeId?: string;
    externalUserId?: string;
  } => {
    const alternatives = Array.isArray(result.alternatives)
      ? (result.alternatives as Record<string, unknown>[])
      : [];
    const firstAlternative = alternatives[0];
    const items = Array.isArray(firstAlternative?.items)
      ? (firstAlternative.items as Record<string, unknown>[])
      : [];

    const candidates = [result, firstAlternative, ...items];

    for (const candidate of candidates) {
      if (!candidate) {
        continue;
      }

      const attendee = candidate.attendee as Maybe<Record<string, unknown>>;
      const attendeeId =
        typeof attendee?.attendeeId === 'string'
          ? attendee.attendeeId
          : typeof candidate.attendeeId === 'string'
            ? candidate.attendeeId
            : undefined;
      const externalUserId =
        typeof attendee?.externalUserId === 'string'
          ? attendee.externalUserId
          : typeof candidate.externalUserId === 'string'
            ? candidate.externalUserId
            : undefined;

      if (attendeeId || externalUserId) {
        return {
          attendeeId,
          externalUserId,
        };
      }
    }

    return {};
  };

  private readonly resolveLocalSpeakerLabel = (): string => {
    return this.formatInterpreterLabel(this.agentNumber);
  };

  /**
   * Subscribes to attendee presence events
   * @private
   */
  private readonly subscribeToAttendeeIdPresence = (): void => {
    this.attendeeIdPresenceHandler = (
      attendeeId: string,
      present: boolean,
      externalUserId?: string,
      dropped?: boolean
    ) => {
      VoiceMeetingEventBus.attendeeIdPresence$.next({
        attendeeId,
        present,
        externalUserId,
        dropped,
      });
    };

    this.meetingSession?.audioVideo.realtimeSubscribeToAttendeeIdPresence(
      this.attendeeIdPresenceHandler
    );
  };

  /**
   * Subscribes to fatal error events
   * @private
   */
  private readonly realtimeSubscribeToFatalError = (): void => {
    this.fatalErrorHandler = (error: Error) => {
      VoiceMeetingEventBus.fatalError$.next(error);
    };

    this.meetingSession?.audioVideo.realtimeSubscribeToFatalError(
      this.fatalErrorHandler
    );
  };

  /**
   * Completely resets the service state to initial values
   * Call this at the end of a call to ensure clean state
   */
  public resetService = (): void => {
    if (this.meetingSession || this.voiceSession) {
      this.tearDownLocalMeetingSession();
    } else {
      this.tearDownListeners();
    }

    this.meetingSession = null;
    this.voiceSession = null;
    this.teamingSpeakerContext = initialTeamingSpeakerContext;
    this.eventObserver = null;
    this.audioVideoObserver = null;
    this.attendeeIdPresenceHandler = null;
    this.fatalErrorHandler = null;
    this.transcriptEventHandler = null;
  };
}
